import { ActivityModel } from './ActivityModel';
import { FetchingState } from '../Fetching/FetchingState';
import { GET_ACTIVITY, GET_MY_ACTIVITY } from '../../queries/Activity';
import { captureException } from '../../../utils/sentry/sentry';
import { clientFunction } from '../../../config/apollo/client';
import { flow, getParent, types } from 'mobx-state-tree';
import { hasError, isReady } from '../Fetching/updateStateHelper';


export interface EntryToEdit {
  activityId: string;
  userActivityId: string;
  /** 1-based challenge day, or null for a free entry */
  day: number | null;
  /** free entry index, or null for a challenge day */
  entryIndex: number | null;
  entry: string;
}

export const ActivityStore = types
  .model('ActivityStore', {
    activities: types.array(types.maybe(ActivityModel)),
    states: types.optional(types.array(FetchingState), []),
  })
  // the entry the user chose to rewrite from the activity page: the diary
  // frames prefill from it and the session end updates it instead of
  // recording a new one. Volatile: never persisted, gone on restart.
  .volatile(() => ({
    entryToEdit: null as EntryToEdit | null,
  }))
  .actions(self => ({
    setEntryToEdit(entryToEdit: EntryToEdit | null) {
      self.entryToEdit = entryToEdit;
    },
  }))
  .actions(self => {
    const reshapeActivity = (activity: any) => {
      return ({
        _id: activity._id,
        type: activity.type,
        title: activity.title,
        shortDescription: activity.shortDescription,
        estimatedTime: activity.estimatedTime,
        image: activity.image,
        designSystemColor: activity.designSystemColor,
        challengeable: activity.challengeable === false ? false : true,
        numberOfDays: activity.numberOfDays,
        sessionId: activity?.session?._id,
        migratedFromExercice: activity.migratedFromExercice || null,
      });
    };

    const loadActivity = (activity: any, language?: string, completelyLoaded?: boolean) => {
      const lang = language ? language : (getParent(self) as any).language.name.key;
      const activityData = {
        ...reshapeActivity(activity),
        completelyLoaded: !!completelyLoaded,
        language: lang,
      };
      const index = self.activities?.findIndex(a => a && a._id === activity._id);
      if (index === -1) {
        self.activities?.push(activityData);
      } else {
        // update in place: replacing the node would detach models that views
        // still reference, and would drop user-activity fields (reminders,
        // days, status) that only fetchMyActivity provides
        const existing = self.activities[index];
        if (existing) {
          const { sessionId, ...rest } = activityData;
          Object.assign(existing, rest);
          // a payload without the session relation must not erase a known id
          if (sessionId) {
            existing.sessionId = sessionId;
          }
        }
      }
    };

    const setReminder = (activityId: string, reminderEnabled: boolean, reminderTime: string | null) => {
      const existing = self.activities?.find(a => a && a._id === activityId);
      if (existing) {
        existing.reminderEnabled = reminderEnabled;
        existing.reminderTime = reminderTime;
      }
    };

    const setUserActivity = (activityId: string, userActivityId: string, status: string, isChallenge: boolean, days?: any[]) => {
      const index = self.activities?.findIndex(a => a && a._id === activityId);
      const existing = index !== -1 ? self.activities[index] : undefined;
      if (existing) {
        existing.userActivityId = userActivityId;
        existing.status = status as any;
        existing.isChallenge = isChallenge;
        if (days) {
          const mapped = days.map(day => ({
            done: !!day.done,
            maxDate: day.maxDate ? new Date(day.maxDate) : null,
            entry: day.entry || null,
            dateOfCompletion: day.dateOfCompletion ? new Date(day.dateOfCompletion) : null,
          }));
          if (existing.days && existing.days.length === mapped.length) {
            // reconcile day-by-day: replacing the array detaches ActivityDayModel
            // nodes that mounted views still read
            mapped.forEach((day, i) => Object.assign(existing.days[i], day));
          } else {
            existing.days = mapped as any;
          }
        }
      }
    };

    const fetchActivity = flow(function* (id: string, language?: string) {
      updateState(id, 'pending');
      const lang = language ? language : (getParent(self) as any).language.name.key;
      try {
        const { data }: any = yield clientFunction(lang)
          .query({ query: GET_ACTIVITY, variables: { 'id': id } });
        if (data) {
          loadActivity({ ...data.activity }, lang, true);
        }
        updateState(id, 'done');
      }
      catch (error) {
        captureException(error);
        updateState(id, 'error');
      }
    });

    const fetchMyActivity = flow(function* (language?: string) {
      const lang = language ? language : (getParent(self) as any).language.name.key;
      try {
        const { data }: any = yield clientFunction(lang).query({ query: GET_MY_ACTIVITY, fetchPolicy: 'no-cache' });
        if (data) {
          const startedIds = new Set();
          data.startedUserActivity.forEach((userActivity: any) => {
            if (userActivity?.activity) {
              startedIds.add(userActivity.activity._id);
              loadActivity(userActivity.activity, lang, true);
              setUserActivity(
                userActivity.activity._id,
                userActivity._id,
                userActivity.status,
                !!userActivity.isChallenge,
                userActivity.days,
              );
              const stored = self.activities.find(a => a && a._id === userActivity.activity._id);
              if (stored) {
                stored.reminderEnabled = !!userActivity.reminderEnabled;
                stored.reminderTime = userActivity.reminderTime || null;
              }
            }
          });
          // reconcile: drop stale local challenge state for activities
          // that are no longer started server-side
          self.activities.forEach(activity => {
            if (activity && activity.userActivityId && activity.status === 'started' && !startedIds.has(activity._id)) {
              activity.userActivityId = null;
              activity.status = undefined;
              activity.isChallenge = undefined;
              activity.days = [] as any;
            }
          });
        }
      } catch (error) {
        captureException(error);
      }
    });

    const fetchActivityIfNotExist = flow(function* (id: string) {
      if (self.activities) {
        const index = self.activities.findIndex(a => a && a._id === id);
        const existing = index === -1 ? undefined : self.activities[index];
        if (!existing || existing.language !== (getParent(self) as any).language.name.key || !existing.completelyLoaded) {
          yield fetchActivity(id);
        }
      }
    });

    const updateState = (id: string, newState: string) => {
      const index = self.states.findIndex(s => s.id === id);
      if (index === -1) {
        self.states.push({ id, state: newState });
      } else {
        self.states[index].state = newState;
      }
    };

    const setError = (id: string) => {
      const index = self.states.findIndex(s => s.id === id);
      if (index !== -1 && self.states[index].state === 'pending') {
        self.states[index].state = 'error';
      }
    };

    const clear = () => {
      self.activities = [] as any;
      self.states = [] as any;
    };

    return {
      loadActivity,
      setReminder,
      setUserActivity,
      fetchActivity,
      fetchActivityIfNotExist,
      fetchMyActivity,
      setError,
      clear,
    };
  })
  .views(self => ({
    getActivity(id: string) {
      const index = self.activities.findIndex(a => a && a._id === id);
      if (index === -1) {
        return null;
      } else {
        return self.activities[index];
      }
    },
    getActivityBySessionId(sessionId: string) {
      const index = self.activities.findIndex(a => a && a.sessionId === sessionId);
      if (index === -1) {
        return null;
      } else {
        return self.activities[index];
      }
    },
    get startedActivities() {
      return self.activities.filter(a => a && a.userActivityId && a.status === 'started');
    },
    isDoneToday(id: string) {
      const index = self.activities.findIndex(a => a && a._id === id);
      const activity = index === -1 ? undefined : self.activities[index];
      if (!activity || !activity.days) {
        return false;
      }
      const today = new Date();
      return activity.days.some(day => day.done && day.dateOfCompletion
        && new Date(day.dateOfCompletion).toDateString() === today.toDateString());
    },
    hasError(id: string) {
      return hasError(self, id);
    },
    isReady(id: string) {
      return isReady(self, id);
    },
  }));
